import { ChevronUp, Loader2 } from 'lucide-react';
import React, {
    type ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { AutomatedReplyStatus } from '@apps/web/components/shared/messages/automated_reply_status';
import { CaregiverMemoryDialog } from '@apps/web/components/shared/messages/caregiver_memory_dialog';
import { findConversationSearchMatches } from '@apps/web/components/shared/messages/conversation_search_utils';
import { ConversationTimeline } from '@apps/web/components/shared/messages/conversation_timeline';
import {
    MessageComposer,
    type MessageComposerSurface,
} from '@apps/web/components/shared/messages/message_composer';
import { MessagePending } from '@apps/web/components/shared/messages/message_pending';
import { type MessageShiftFootnote } from '@apps/web/components/shared/messages/message_shift_footnotes';
import {
    buildConversationTimelineEntries,
    countRecentDayTimelineEntries,
} from '@apps/web/components/shared/messages/messages_timeline_utils';
import { Button } from '@apps/web/components/ui/button';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { CallDetailDialog } from '@apps/web/components/voice_calls/call_detail_dialog';
import {
    useOrganizationConversationRealtime,
    useOrganizationPendingMessagesRealtime,
} from '@apps/web/hooks/realtime_hooks';
import { useComposerFooterHeight } from '@apps/web/hooks/use_composer_footer_height';
import {
    formatTypingLabel,
    useConversationTyping,
} from '@apps/web/hooks/use_conversation_typing';
import { useOrganizationReconnectRecovery } from '@apps/web/hooks/use_organization_reconnect_recovery';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { isS3RecordingUrl } from '@apps/web/lib/contact_utils';
import { cn } from '@apps/web/lib/utils';
import { readConversationAuditEvents } from '@apps/web/swr_data_fetching/conversation_ownership';
import {
    useShiftConfirmation,
    useSmsEngineRunsByMessageIds,
    useSmsRunRatings,
    useUser,
    useVoiceCallRecordingUrls,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type Message,
    type MessageRatingLabel,
    type OrganizationConversation,
    type StagedMessage,
} from '@libraries/typescript/api_client';

// Messages and the composer share one centered reading column so a thread stays
// legible on a wide monitor instead of stretching bubbles across the pane. In
// narrow embeds (caregiver tab, extension side panel) the cap never binds.
const THREAD_COLUMN_MAX_WIDTH_CLASS = 'max-w-[67rem]';

type MessagesPaneProps = {
    readonly timezone: string;
    readonly organizationId?: string | null;
    readonly caregiverName?: string | null;
    readonly domain?: 'shift_confirmation';
    readonly domainId?: string | null;
    readonly conversationId?: string | null;
    readonly refreshOnOpen?: boolean;
    readonly highlightMessageId?: string | null;
    readonly highlightLabel?: string;
    readonly showComposer?: boolean;
    readonly showRating?: boolean;
    // Coordinator SMS training review: thumbs on Phoebe replies in-thread.
    readonly smsTrainingReview?: boolean;
    readonly markAsRead?: boolean;
    readonly isActive?: boolean;
    readonly messageAnalyticsSurface?: MessageComposerSurface;
    readonly autoScrollToHighlight?: boolean;
    readonly autoScrollToLatest?: boolean;
    readonly scrollToHighlightFromLatest?: boolean;
    // Collapse a long thread down to just the most recent day's messages on
    // open, behind a "Show earlier messages" control, so the relevant tail is
    // visible without scrolling. Auto-expands when a highlighted message falls
    // outside the recent-day window.
    readonly collapseToRecentDay?: boolean;
    readonly renderScrollHeader?: (
        conversation: OrganizationConversation | null,
    ) => ReactNode;
    // In-conversation keyword search ("search this chat"). When active, the
    // whole conversation is loaded so every match is rendered, the term is
    // highlighted, and the active match is scrolled into view.
    readonly searchActive?: boolean;
    readonly searchQuery?: string | null;
    readonly activeSearchMatchId?: string | null;
    readonly onSearchMatchIdsChange?: (matchIds: Array<string>) => void;
    readonly showConversationAuditEvents?: boolean;
    readonly showAutomatedReplyControls?: boolean;
    readonly showMessageNotes?: boolean;
    // Mockup-only: drop the 67rem reading-column cap and the 680px bubble
    // cap so the thread fills a wide pane (e.g. after a right rail is gone).
    readonly fillThreadWidth?: boolean;
    // Mockup-only: skip live conversation fetches and render these messages
    // instead. Used for synthetic demo thread IDs that are not in the API.
    readonly overrideMessages?: ReadonlyArray<
        Message & {
            readonly shift_footnotes?: ReadonlyArray<MessageShiftFootnote> | null;
        }
    >;
};

const PENDING_OUTBOUND_REFRESH_INTERVAL_MS = 2000;

// Stable empty match list so a closed search does not churn the parent on every
// realtime message update.
const NO_SEARCH_MATCHES: Array<string> = [];

export function MessagesPane({
    timezone,
    organizationId,
    caregiverName,
    domain,
    domainId,
    conversationId: providedConversationId,
    refreshOnOpen = false,
    highlightMessageId,
    highlightLabel = 'Highlighted',
    showComposer = true,
    showRating = true,
    smsTrainingReview = false,
    markAsRead = true,
    isActive = true,
    messageAnalyticsSurface,
    autoScrollToHighlight = true,
    autoScrollToLatest = true,
    scrollToHighlightFromLatest = false,
    collapseToRecentDay = false,
    renderScrollHeader,
    searchActive = false,
    searchQuery = null,
    activeSearchMatchId = null,
    onSearchMatchIdsChange,
    showConversationAuditEvents = false,
    showAutomatedReplyControls = false,
    showMessageNotes = false,
    fillThreadWidth = false,
    overrideMessages,
}: MessagesPaneProps) {
    const { formatName } = useFormatPersonName();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const highlightedMessageRef = useRef<HTMLDivElement>(null);
    // The active search match we last centered after the conversation finished
    // loading — used to avoid re-centering on every later realtime message.
    const lastSettledSearchMatchIdRef = useRef<string | null>(null);
    const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
    const [selectedMemoryCaregiverId, setSelectedMemoryCaregiverId] = useState<
        string | null
    >(null);
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const topSentinelRef = useRef<HTMLDivElement | null>(null);
    const previousScrollMetricsRef = useRef<{
        height: number;
        scrollTop: number;
    } | null>(null);
    const loadingOlderRef = useRef(false);
    const skipAutoScrollRef = useRef(false);
    const hasScrollInteractionRef = useRef(false);
    const initialLoadCompleteRef = useRef(false);
    const [historyExpanded, setHistoryExpanded] = useState(false);
    // Scroll metrics captured right before expanding collapsed history so the
    // revealed older messages are prepended without yanking the viewport away
    // from what the user was reading.
    const expandAnchorRef = useRef<{
        height: number;
        scrollTop: number;
    } | null>(null);

    const { data: shiftConfirmation, mutate: mutateShiftConfirmation } =
        useShiftConfirmation(
            organizationId ?? null,
            domain === 'shift_confirmation' ? (domainId ?? null) : null,
            { reason: 'messages-pane' },
        );

    const conversationId =
        providedConversationId ?? shiftConfirmation?.conversation_id ?? null;
    const liveConversationId =
        overrideMessages === undefined ? conversationId : null;

    const shouldUseConversationPendingMessages =
        conversationId !== null && !domain;

    // Search still walks older pages for matches. A highlight only needs
    // pages until that message is loaded — not the rest of the thread.
    const isSearching = searchActive && (searchQuery ?? '').trim().length > 0;

    const {
        conversation,
        messages: fetchedMessages,
        isLoading,
        isValidating,
        error,
        isLoadingMore,
        isReachingEnd,
        setSize,
        mutate: mutateConversation,
    } = useOrganizationConversationRealtime(liveConversationId, {
        autoUpdate: true,
        markAsRead,
        organizationId,
        autoLoadPages: isSearching,
        autoLoadUntilMessageId: isSearching ? null : highlightMessageId,
    });

    const {
        data: pendingMessagesFromApi,
        isLoading: isLoadingPendingMessagesFromApi,
        mutate: mutatePendingMessagesFromApi,
    } = useOrganizationPendingMessagesRealtime(
        'messages-pane',
        conversationId === null || shouldUseConversationPendingMessages,
        {
            autoUpdate: conversationId !== null,
            ...(domain && { domain }),
            ...(domainId && { domainId }),
            ...(conversationId && { conversationId }),
        },
    );

    const stagedMessages = useMemo(
        () =>
            shouldUseConversationPendingMessages
                ? (conversation?.pending_messages ?? [])
                : pendingMessagesFromApi,
        [
            shouldUseConversationPendingMessages,
            conversation?.pending_messages,
            pendingMessagesFromApi,
        ],
    );
    const isLoadingStagedMessages = shouldUseConversationPendingMessages
        ? false
        : isLoadingPendingMessagesFromApi;
    const isMessagesPaneLoading =
        overrideMessages !== undefined
            ? false
            : isLoading || isLoadingStagedMessages;
    const mutateStagedMessages = useCallback(
        () =>
            shouldUseConversationPendingMessages
                ? void mutateConversation()
                : void mutatePendingMessagesFromApi(),
        [
            shouldUseConversationPendingMessages,
            mutateConversation,
            mutatePendingMessagesFromApi,
        ],
    );
    const refreshConversationPane = useCallback(async () => {
        if (!organizationId) {
            return;
        }

        const refreshes: Array<Promise<unknown>> = [];
        if (domain === 'shift_confirmation' && domainId) {
            refreshes.push(mutateShiftConfirmation());
        }

        if (conversationId && overrideMessages === undefined) {
            refreshes.push(mutateConversation());
        }

        if (!shouldUseConversationPendingMessages) {
            refreshes.push(mutatePendingMessagesFromApi());
        }
        await Promise.allSettled(refreshes);
    }, [
        conversationId,
        domain,
        domainId,
        mutateConversation,
        mutatePendingMessagesFromApi,
        mutateShiftConfirmation,
        organizationId,
        overrideMessages,
        shouldUseConversationPendingMessages,
    ]);
    const throttledRefreshConversationPane = useThrottledCallback(
        refreshConversationPane,
        1000,
    );
    useOrganizationReconnectRecovery({
        organizationId: organizationId ?? null,
        recoveryLabel: `messages-pane:${conversationId ?? domainId ?? 'none'}`,
        priority: 20,
        recover: refreshConversationPane,
        enabled: isActive,
    });

    const messages = useMemo(
        () =>
            overrideMessages !== undefined
                ? [...overrideMessages]
                : (fetchedMessages ?? []),
        [fetchedMessages, overrideMessages],
    );

    const searchMatchIds = useMemo(
        () =>
            searchActive
                ? findConversationSearchMatches(messages, searchQuery ?? '')
                : NO_SEARCH_MATCHES,
        [searchActive, messages, searchQuery],
    );

    // Push matches up before paint so the search bar's "N of M" count never
    // shows a stale total for a frame after the keyword changes (the matches
    // and the highlights are recomputed synchronously here).
    useLayoutEffect(() => {
        onSearchMatchIdsChange?.(searchMatchIds);
    }, [searchMatchIds, onSearchMatchIdsChange]);

    // Scroll the active match into view. While the conversation is still
    // auto-loading older pages (matches can live in not-yet-loaded pages),
    // re-anchor instantly on each batch so prepended pages don't push the match
    // out of view; settle with a smooth scroll once everything is loaded. The
    // active match is found by data attribute so a not-yet-loaded match is a
    // no-op that simply retries once more pages arrive. Once everything is
    // loaded and this match has been centered, stop reacting to `messages` so a
    // later incoming realtime message doesn't yank the viewport back while the
    // user is reading around the match.
    useEffect(() => {
        if (!searchActive || !activeSearchMatchId) {
            lastSettledSearchMatchIdRef.current = null;
            return;
        }
        if (
            isReachingEnd &&
            lastSettledSearchMatchIdRef.current === activeSearchMatchId
        ) {
            return;
        }
        const root = scrollAreaRef.current;
        if (!root) {
            return;
        }
        const element = root.querySelector<HTMLElement>(
            '[data-search-active="true"]',
        );
        if (!element) {
            return;
        }
        element.scrollIntoView({
            behavior: isReachingEnd ? 'smooth' : 'instant',
            block: 'center',
        });
        if (isReachingEnd) {
            lastSettledSearchMatchIdRef.current = activeSearchMatchId;
        }
    }, [searchActive, activeSearchMatchId, messages, isReachingEnd]);

    const hasPendingOutboundMessages = useMemo(
        () =>
            messages.some(
                (message) =>
                    message.role === 'assistant' &&
                    (message.sent_status ?? 'sent') === 'pending',
            ),
        [messages],
    );
    const calls = useMemo(
        () => conversation?.calls ?? [],
        [conversation?.calls],
    );
    const ledgerEvents = useMemo(
        () => conversation?.ledger_events ?? [],
        [conversation?.ledger_events],
    );
    const auditEvents = useMemo(
        () =>
            showConversationAuditEvents
                ? readConversationAuditEvents(conversation)
                : [],
        [conversation, showConversationAuditEvents],
    );
    const ehrClockEvents = useMemo(
        () => conversation?.ehr_clock_events ?? [],
        [conversation?.ehr_clock_events],
    );
    const recordingVoiceCallIds = useMemo(() => {
        const ids = calls
            .filter((call) => isS3RecordingUrl(call.recording_url))
            .map((call) => call.id);
        return ids.length > 0 ? ids : null;
    }, [calls]);
    const { data: recordingUrls } = useVoiceCallRecordingUrls(
        organizationId ?? null,
        recordingVoiceCallIds,
    );
    const timelineEntries = useMemo(
        () =>
            buildConversationTimelineEntries(
                messages,
                calls,
                ledgerEvents,
                auditEvents,
                ehrClockEvents,
            ),
        [messages, calls, ledgerEvents, auditEvents, ehrClockEvents],
    );

    const recentDayVisibleCount = useMemo(
        () =>
            collapseToRecentDay
                ? countRecentDayTimelineEntries(timelineEntries, timezone)
                : timelineEntries.length,
        [collapseToRecentDay, timelineEntries, timezone],
    );

    // Auto-expand when a highlighted message lives outside the recent-day
    // window so the scroll-to-highlight below can reach it. Messages load
    // newest-first, so a highlight that is not yet in the loaded timeline must
    // be older than what we have; expanding lets pagination fetch it.
    const shouldExpandForHighlight = useMemo(() => {
        if (!collapseToRecentDay || !highlightMessageId) {
            return false;
        }
        const highlightIndex = timelineEntries.findIndex(
            (entry) =>
                entry.type === 'message' &&
                entry.message.id === highlightMessageId,
        );
        if (highlightIndex === -1) {
            return true;
        }
        return highlightIndex < timelineEntries.length - recentDayVisibleCount;
    }, [
        collapseToRecentDay,
        highlightMessageId,
        timelineEntries,
        recentDayVisibleCount,
    ]);

    const shouldCollapseHistory =
        collapseToRecentDay &&
        !searchActive &&
        !historyExpanded &&
        !shouldExpandForHighlight &&
        timelineEntries.length > recentDayVisibleCount;

    const visibleTimelineEntries = useMemo(
        () =>
            shouldCollapseHistory
                ? timelineEntries.slice(
                      timelineEntries.length - recentDayVisibleCount,
                  )
                : timelineEntries,
        [shouldCollapseHistory, timelineEntries, recentDayVisibleCount],
    );

    const { data: user } = useUser({ reason: 'messages-pane' });
    const typingConversationId =
        (liveConversationId || conversation?.id) ?? null;
    const {
        typingUsers: conversationTypingUsers,
        notifyTyping: notifyConversationTyping,
    } = useConversationTyping({
        organizationId: organizationId ?? null,
        conversationId: typingConversationId,
        currentUserId: user?.id ?? null,
        enabled: showComposer,
    });
    const firstTypingUser = conversationTypingUsers[0] ?? null;
    const messageIdsForRuns = useMemo(
        () =>
            overrideMessages !== undefined
                ? []
                : messages.map((message) => message.id),
        [messages, overrideMessages],
    );
    const { runIdByMessageId } = useSmsEngineRunsByMessageIds(
        messageIdsForRuns,
        Boolean(user?.admin),
        'messages-pane',
    );
    // Coordinator SMS training review: map each engine run to the inbound
    // message that triggered it + rating, so that message can be thumbed in the
    // thread. The run completes on the inbound and it is always present (a run
    // that only takes actions sends no reply), so anchor there.
    const { data: smsRunRatingsData, mutate: mutateSmsRunRatings } =
        useSmsRunRatings(
            organizationId ?? null,
            liveConversationId,
            smsTrainingReview && liveConversationId !== null,
            'messages-pane-sms-review',
        );
    const handleSmsRunRated = useCallback(
        () => void mutateSmsRunRatings(),
        [mutateSmsRunRatings],
    );
    const smsRunRatingsByMessageId = useMemo(() => {
        const map: Record<
            string,
            Array<{ run_id: string; rating_label: MessageRatingLabel | null }>
        > = {};
        for (const runRating of smsRunRatingsData?.run_ratings ?? []) {
            // Anchor the thumb on the inbound that triggered the run — it is
            // always present, even when the run only takes actions and sends
            // no reply.
            const anchorMessageId = runRating.message_id;
            const ratingsForMessage = (map[anchorMessageId] ??= []);
            ratingsForMessage.push({
                run_id: runRating.run_id,
                rating_label: runRating.rating_label,
            });
        }
        return map;
    }, [smsRunRatingsData]);

    const latestTimelineEntryId = useMemo(
        () => timelineEntries[timelineEntries.length - 1]?.id ?? null,
        [timelineEntries],
    );
    const latestReplyPreviewId =
        conversation?.automated_reply_preview?.slice(-1)[0]?.id;

    useEffect(() => {
        loadingOlderRef.current = false;
        skipAutoScrollRef.current = false;
        hasScrollInteractionRef.current = false;
        previousScrollMetricsRef.current = null;
        initialLoadCompleteRef.current = false;
        expandAnchorRef.current = null;
        setHistoryExpanded(false);
        setSelectedCallId(null);
        setSelectedMemoryCaregiverId(null);
    }, [conversationId]);

    const getViewportElement = useCallback(() => {
        const root = scrollAreaRef.current;
        if (!root) {
            return null;
        }
        return root.querySelector<HTMLElement>(
            '[data-slot="scroll-area-viewport"]',
        );
    }, []);

    const handleShowEarlierMessages = useCallback(() => {
        const viewport = getViewportElement();
        if (viewport) {
            expandAnchorRef.current = {
                height: viewport.scrollHeight,
                scrollTop: viewport.scrollTop,
            };
        }
        setHistoryExpanded(true);
    }, [getViewportElement]);

    // Keep the reading position steady when older messages are revealed: the
    // newly rendered history is prepended above, so shift scrollTop by the
    // height it added.
    useLayoutEffect(() => {
        if (!historyExpanded) {
            return;
        }
        const anchor = expandAnchorRef.current;
        expandAnchorRef.current = null;
        if (!anchor) {
            return;
        }
        const viewport = getViewportElement();
        if (!viewport) {
            return;
        }
        const heightDelta = viewport.scrollHeight - anchor.height;
        viewport.scrollTop = anchor.scrollTop + heightDelta;
    }, [historyExpanded, getViewportElement]);

    const requestOlderMessages = useCallback(() => {
        if (isLoadingMore || isReachingEnd) {
            return;
        }

        // Older pages stay hidden while history is collapsed; the user reveals
        // them via "Show earlier messages" first.
        if (shouldCollapseHistory) {
            return;
        }

        if (!initialLoadCompleteRef.current) {
            return;
        }

        if (!hasScrollInteractionRef.current) {
            return;
        }

        const viewport = getViewportElement();
        if (!viewport) {
            return;
        }

        loadingOlderRef.current = true;
        skipAutoScrollRef.current = true;
        previousScrollMetricsRef.current = {
            height: viewport.scrollHeight,
            scrollTop: viewport.scrollTop,
        };

        void setSize((currentSize) => currentSize + 1);
    }, [
        isLoadingMore,
        isReachingEnd,
        shouldCollapseHistory,
        getViewportElement,
        setSize,
    ]);

    useEffect(() => {
        if (isMessagesPaneLoading || error) {
            return;
        }

        const viewport = getViewportElement();
        const root = scrollAreaRef.current;
        if (!viewport || !root) {
            return;
        }

        const markScrollInteraction = () => {
            hasScrollInteractionRef.current = true;
        };

        viewport.addEventListener('wheel', markScrollInteraction, {
            passive: true,
        });
        viewport.addEventListener('touchstart', markScrollInteraction, {
            passive: true,
        });
        viewport.addEventListener('keydown', markScrollInteraction);
        root.addEventListener('pointerdown', markScrollInteraction);

        return () => {
            viewport.removeEventListener('wheel', markScrollInteraction);
            viewport.removeEventListener('touchstart', markScrollInteraction);
            viewport.removeEventListener('keydown', markScrollInteraction);
            root.removeEventListener('pointerdown', markScrollInteraction);
        };
    }, [conversationId, error, getViewportElement, isMessagesPaneLoading]);

    useEffect(() => {
        if (isMessagesPaneLoading || error) {
            return;
        }

        const sentinel = topSentinelRef.current;
        const viewport = getViewportElement();
        if (!sentinel || !viewport) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry?.isIntersecting) {
                    return;
                }
                if (!initialLoadCompleteRef.current) {
                    return;
                }
                requestOlderMessages();
            },
            {
                root: viewport,
                rootMargin: '0px 0px -80% 0px',
                threshold: 0,
            },
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [
        error,
        getViewportElement,
        isMessagesPaneLoading,
        requestOlderMessages,
    ]);

    useEffect(() => {
        if (!loadingOlderRef.current || isLoadingMore) {
            return;
        }

        const viewport = getViewportElement();
        const previousMetrics = previousScrollMetricsRef.current;

        loadingOlderRef.current = false;
        previousScrollMetricsRef.current = null;

        if (!viewport || !previousMetrics) {
            return;
        }

        // While searching, the active match owns the scroll position. Skip the
        // pre-load anchor restore (this effect runs after the search-centering
        // effect) so a completing older-load doesn't push the match off-screen.
        if (searchActive && activeSearchMatchId) {
            return;
        }

        const heightDelta = viewport.scrollHeight - previousMetrics.height;
        viewport.scrollTop = previousMetrics.scrollTop + heightDelta;
    }, [
        getViewportElement,
        isLoadingMore,
        messages,
        searchActive,
        activeSearchMatchId,
    ]);

    useEffect(() => {
        // If there's a highlighted message, scroll to it instead of the bottom
        if (
            autoScrollToHighlight &&
            highlightMessageId &&
            highlightedMessageRef.current
        ) {
            const scrollToHighlightedMessage = () => {
                highlightedMessageRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                initialLoadCompleteRef.current = true;
            };

            const viewport = getViewportElement();
            if (scrollToHighlightFromLatest && autoScrollToLatest && viewport) {
                requestAnimationFrame(() => {
                    viewport.scrollTo({
                        top: viewport.scrollHeight,
                        behavior: 'instant',
                    });
                    setTimeout(scrollToHighlightedMessage, 100);
                });
                return;
            }

            setTimeout(scrollToHighlightedMessage, 100);
            return;
        }

        if (skipAutoScrollRef.current) {
            skipAutoScrollRef.current = false;
            initialLoadCompleteRef.current = true;
            return;
        }

        if (activeSearchMatchId) {
            // When in-chat search has an active match, it owns scroll
            // positioning (it centers that match), so don't jump to the latest
            // message when one arrives. With no active match — an empty query or
            // a query with no results — new messages scroll into view normally.
            initialLoadCompleteRef.current = true;
            return;
        }

        if (!autoScrollToLatest) {
            if (initialLoadCompleteRef.current) {
                return;
            }

            initialLoadCompleteRef.current = true;
            return;
        }

        if (
            latestTimelineEntryId ||
            latestReplyPreviewId ||
            (stagedMessages?.length ?? 0) > 0
        ) {
            const viewport = getViewportElement();
            if (!viewport) {
                return;
            }

            requestAnimationFrame(() => {
                viewport.scrollTo({
                    top: viewport.scrollHeight,
                    behavior: 'instant',
                });
                initialLoadCompleteRef.current = true;
            });
        }
    }, [
        autoScrollToHighlight,
        autoScrollToLatest,
        conversationId,
        latestTimelineEntryId,
        latestReplyPreviewId,
        highlightMessageId,
        getViewportElement,
        scrollToHighlightFromLatest,
        activeSearchMatchId,
        stagedMessages?.length,
    ]);

    useEffect(() => {
        if (!refreshOnOpen || !isActive || overrideMessages !== undefined) {
            return;
        }

        throttledRefreshConversationPane();
    }, [
        isActive,
        overrideMessages,
        refreshOnOpen,
        throttledRefreshConversationPane,
    ]);

    useEffect(() => {
        if (!isActive || !organizationId) {
            return;
        }

        const revalidateIfVisible = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            throttledRefreshConversationPane();
        };

        window.addEventListener('focus', revalidateIfVisible);
        document.addEventListener('visibilitychange', revalidateIfVisible);

        return () => {
            window.removeEventListener('focus', revalidateIfVisible);
            document.removeEventListener(
                'visibilitychange',
                revalidateIfVisible,
            );
        };
    }, [isActive, organizationId, throttledRefreshConversationPane]);

    useEffect(() => {
        if (
            !isActive ||
            !organizationId ||
            !conversationId ||
            !hasPendingOutboundMessages
        ) {
            return;
        }

        const intervalId = window.setInterval(() => {
            throttledRefreshConversationPane();
        }, PENDING_OUTBOUND_REFRESH_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [
        conversationId,
        hasPendingOutboundMessages,
        isActive,
        organizationId,
        throttledRefreshConversationPane,
    ]);

    const setComposerFooterNode = useComposerFooterHeight();

    if (isMessagesPaneLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
        );
    }

    async function retryMessages() {
        try {
            await mutateConversation();
        } catch {
            // SWR retains the failure for the inline notice and another retry.
        }
    }

    const accessDenied =
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403);
    const showCachedConversation = Boolean(conversation) && !accessDenied;
    const messageLoadError =
        error && overrideMessages === undefined ? (
            <div
                className="text-muted-foreground flex shrink-0 items-center justify-center gap-3 p-3 text-sm"
                role="alert"
            >
                <p>
                    {showCachedConversation
                        ? 'Couldn’t refresh messages. Showing the last loaded messages.'
                        : 'Couldn’t load messages.'}
                </p>
                <Button
                    aria-label="Retry loading messages"
                    disabled={isValidating}
                    onClick={() => void retryMessages()}
                    size="sm"
                    type="button"
                    variant="outline"
                >
                    {isValidating ? 'Retrying…' : 'Retry'}
                </Button>
            </div>
        ) : null;

    if (messageLoadError && !showCachedConversation) {
        return (
            <div className="flex h-full items-center justify-center">
                {messageLoadError}
            </div>
        );
    }

    const shouldShowComposer =
        showComposer && organizationId && (conversationId || conversation?.id);

    const hasTimelineEntries = timelineEntries.length > 0;
    const scrollHeader = renderScrollHeader?.(conversation);

    const messagesContainerClassName = cn(
        'flex w-full flex-1 flex-col justify-end gap-4 px-4 pt-4',
        fillThreadWidth ? null : cn('mx-auto', THREAD_COLUMN_MAX_WIDTH_CLASS),
        shouldShowComposer ? 'pb-6' : 'pb-4',
    );

    return (
        <div className="flex h-full min-h-0 flex-col">
            {messageLoadError}
            <ScrollArea
                ref={scrollAreaRef}
                className="min-h-0 flex-1"
                viewportClassName="[&>div]:!flex [&>div]:min-h-full [&>div]:flex-col"
            >
                <div
                    className="relative flex min-h-full flex-1 flex-col"
                    data-testid="messages-scroll-content"
                >
                    {scrollHeader ? (
                        <div
                            ref={topSentinelRef}
                            className="absolute top-0 left-0 h-px w-full"
                        />
                    ) : null}
                    {scrollHeader}
                    <div
                        className={messagesContainerClassName}
                        data-testid="messages-container"
                    >
                        {!scrollHeader ? (
                            <div ref={topSentinelRef} className="h-px w-full" />
                        ) : null}
                        {isLoadingMore && hasTimelineEntries && (
                            <div className="flex justify-center">
                                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                            </div>
                        )}
                        {shouldCollapseHistory && (
                            <div className="flex justify-center pt-1">
                                <button
                                    type="button"
                                    onClick={handleShowEarlierMessages}
                                    className="text-muted-foreground hover:text-foreground bg-background hover:bg-muted/50 flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                                >
                                    <ChevronUp className="h-3.5 w-3.5" />
                                    Show earlier messages
                                </button>
                            </div>
                        )}
                        {hasTimelineEntries ? (
                            <ConversationTimeline
                                entries={visibleTimelineEntries}
                                fillThreadWidth={fillThreadWidth}
                                timezone={timezone}
                                highlightedMessageRef={highlightedMessageRef}
                                highlightMessageId={highlightMessageId}
                                highlightLabel={highlightLabel}
                                searchActive={searchActive}
                                searchQuery={searchQuery}
                                activeSearchMatchId={activeSearchMatchId}
                                showRating={showRating}
                                recordingUrls={recordingUrls}
                                onOpenCall={setSelectedCallId}
                                onOpenCaregiverMemory={
                                    setSelectedMemoryCaregiverId
                                }
                                runIdByMessageId={runIdByMessageId}
                                organizationId={organizationId}
                                smsTrainingReview={smsTrainingReview}
                                smsRunRatingsByMessageId={
                                    smsRunRatingsByMessageId
                                }
                                onSmsRunRated={handleSmsRunRated}
                                contactName={
                                    formatName(caregiverName) ||
                                    formatName(conversation?.caregiver_name) ||
                                    conversation?.external_contact_name ||
                                    conversation?.phone_number ||
                                    null
                                }
                                contactColorKey={
                                    conversation?.id ??
                                    conversationId ??
                                    undefined
                                }
                                showMessageNotes={showMessageNotes}
                                openNotesForMessageId={highlightMessageId}
                                onConversationMutated={() =>
                                    mutateConversation()
                                }
                            />
                        ) : (
                            <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
                                <h4 className="mb-2 text-lg font-semibold">
                                    No Messages Yet!
                                </h4>
                                <p className="text-muted-foreground text-sm">
                                    Communication history will appear here
                                </p>
                            </div>
                        )}
                        {conversation && (
                            <AutomatedReplyStatus
                                key={conversation.id}
                                conversation={conversation}
                                organizationId={organizationId ?? null}
                                showControls={showAutomatedReplyControls}
                                onUpdate={mutateConversation}
                            />
                        )}
                        {conversationId !== null &&
                            stagedMessages &&
                            stagedMessages.length > 0 && (
                                <React.Fragment>
                                    <div className="my-4 flex items-center gap-2">
                                        <div className="bg-border h-px flex-1" />
                                        <span className="text-muted-foreground text-xs">
                                            Pending Approval
                                        </span>
                                        <div className="bg-border h-px flex-1" />
                                    </div>
                                    {organizationId &&
                                        stagedMessages &&
                                        stagedMessages.map(
                                            (stagedMessage: StagedMessage) => (
                                                <MessagePending
                                                    key={stagedMessage.id}
                                                    stagedMessage={
                                                        stagedMessage
                                                    }
                                                    timezone={timezone}
                                                    organizationId={
                                                        organizationId
                                                    }
                                                    onUpdate={
                                                        mutateStagedMessages
                                                    }
                                                />
                                            ),
                                        )}
                                </React.Fragment>
                            )}
                        <div ref={messagesEndRef} id="messages-end" />
                    </div>
                </div>
            </ScrollArea>
            {shouldShowComposer && (
                <div
                    ref={setComposerFooterNode}
                    data-testid="messages-composer-footer"
                    className="bg-background sticky bottom-0 z-10 flex-shrink-0 border-t pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] md:static md:border-t-0 md:py-4"
                >
                    <div
                        className={cn(
                            'w-full px-4',
                            fillThreadWidth
                                ? null
                                : cn('mx-auto', THREAD_COLUMN_MAX_WIDTH_CLASS),
                        )}
                        data-testid="messages-composer-content"
                    >
                        {firstTypingUser ? (
                            <div className="text-muted-foreground mb-1 flex items-center gap-1.5 px-1 text-xs">
                                <UserAvatar
                                    displayName={firstTypingUser.displayName}
                                    colorKey={firstTypingUser.userId}
                                />
                                <span>
                                    {formatTypingLabel(conversationTypingUsers)}
                                </span>
                            </div>
                        ) : null}
                        <MessageComposer
                            analyticsSurface={messageAnalyticsSurface}
                            timezone={timezone}
                            organizationId={organizationId}
                            conversationId={
                                (conversationId || conversation?.id) ?? ''
                            }
                            onSent={refreshConversationPane}
                            onTyping={notifyConversationTyping}
                        />
                    </div>
                </div>
            )}
            {organizationId && (
                <CallDetailDialog
                    caregiverName={
                        formatName(
                            caregiverName ??
                                conversation?.caregiver_name ??
                                null,
                        ) || null
                    }
                    organizationId={organizationId}
                    voiceCallId={selectedCallId}
                    open={selectedCallId !== null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setSelectedCallId(null);
                        }
                    }}
                    timezone={timezone}
                />
            )}
            {organizationId && (
                <CaregiverMemoryDialog
                    organizationId={organizationId}
                    caregiverId={selectedMemoryCaregiverId}
                    onClose={() => setSelectedMemoryCaregiverId(null)}
                />
            )}
        </div>
    );
}
